import type { ComputedRef, InjectionKey } from "vue";
import { ComponentDefinitionIds } from "./componentDefinitionIds";

export interface AssessmentOption {
  id: string;
  name?: string;
  value?: number;
  description?: string;
}

export interface AssessmentBlockDefinition {
  id?: string;
  componentId?: string;
  name?: string;
  title?: string;
  label?: string;
  placeholder?: string;
  options?: AssessmentOption[];
  children?: AssessmentBlockDefinition[];
  [key: string]: unknown;
}

export interface AssessmentAnswerPayload {
  type: string;
  value: unknown;
  path: string;
  binding: AssessmentBlockDefinition;
  data: Record<string, unknown>;
}

export interface AnswerableAssessmentBlockSummary {
  id: string;
  componentId: string;
  answerType: "single-choice" | "multi-choice" | "text" | "number";
  label: string;
  path: string;
  optionCount?: number;
  options?: AssessmentOption[];
}

export const CandidateAssessmentBlocksKey: InjectionKey<ComputedRef<AnswerableAssessmentBlockSummary[]>> =
  Symbol("candidate-assessment-blocks");

const ASSESSMENT_ANSWER_TYPES: Record<string, AnswerableAssessmentBlockSummary["answerType"]> = {
  [ComponentDefinitionIds.RADIO_GROUP]: "single-choice",
  [ComponentDefinitionIds.SELECT_INPUT]: "single-choice",
  [ComponentDefinitionIds.CHECKBOX_GROUP]: "multi-choice",
  [ComponentDefinitionIds.TEXT_INPUT]: "text",
  [ComponentDefinitionIds.NUMBER_INPUT]: "number",
};

const ASSESSMENT_COMPONENT_IDS = new Set(Object.keys(ASSESSMENT_ANSWER_TYPES));

export function isAssessmentAnswerComponentId(componentId?: string | null): componentId is string {
  return typeof componentId === "string" && ASSESSMENT_COMPONENT_IDS.has(componentId);
}

function optionsFor(block: AssessmentBlockDefinition): AssessmentOption[] {
  return Array.isArray(block.options) ? block.options : [];
}

function selectedOption(block: AssessmentBlockDefinition, value: unknown): AssessmentOption | undefined {
  const id = typeof value === "string" ? value : String(value ?? "");
  return optionsFor(block).find((option) => option.id === id);
}

function scoreFor(options: AssessmentOption[]): number {
  return options.reduce((sum, option) => sum + (option.value || 0), 0);
}

export function buildAssessmentAnswerPayload(options: {
  block: AssessmentBlockDefinition;
  componentId?: string;
  value: unknown;
  path: string;
}): AssessmentAnswerPayload {
  const componentId = options.componentId ?? options.block.componentId;
  if (!isAssessmentAnswerComponentId(componentId)) {
    throw new Error(`Unsupported assessment answer component: ${componentId ?? "unknown"}`);
  }

  switch (componentId) {
    case ComponentDefinitionIds.CHECKBOX_GROUP: {
      const value = Array.isArray(options.value) ? options.value.map(String) : [];
      const selected = optionsFor(options.block).filter((option) => value.includes(option.id));
      return {
        type: componentId,
        value,
        path: options.path,
        binding: options.block,
        data: {
          options: selected,
          score: scoreFor(selected),
        },
      };
    }
    case ComponentDefinitionIds.RADIO_GROUP:
    case ComponentDefinitionIds.SELECT_INPUT: {
      const value = typeof options.value === "string" ? options.value : String(options.value ?? "");
      const option = selectedOption(options.block, value);
      return {
        type: componentId,
        value,
        path: options.path,
        binding: options.block,
        data: {
          option,
          score: option?.value ?? 0,
        },
      };
    }
    case ComponentDefinitionIds.TEXT_INPUT: {
      const value = typeof options.value === "string" ? options.value : String(options.value ?? "");
      return {
        type: componentId,
        value,
        path: options.path,
        binding: options.block,
        data: { text: value },
      };
    }
    case ComponentDefinitionIds.NUMBER_INPUT: {
      const value = options.value === null || options.value === undefined || options.value === ""
        ? ""
        : String(options.value);
      return {
        type: componentId,
        value,
        path: options.path,
        binding: options.block,
        data: { value: options.value },
      };
    }
  }
}

export function normaliseAssessmentToolResultValue(componentId: string | null | undefined, value: unknown): unknown {
  if (componentId === ComponentDefinitionIds.NUMBER_INPUT) {
    const numberValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numberValue) ? numberValue : value;
  }
  return value;
}

export function collectAnswerableAssessmentBlocks(
  content: AssessmentBlockDefinition[] | null | undefined,
  resolvePath: (id: string) => string = () => "",
): AnswerableAssessmentBlockSummary[] {
  const result: AnswerableAssessmentBlockSummary[] = [];

  const visit = (blocks: AssessmentBlockDefinition[] | undefined) => {
    if (!Array.isArray(blocks)) {
      return;
    }

    for (const block of blocks) {
      if (block.id && isAssessmentAnswerComponentId(block.componentId)) {
        const options = optionsFor(block);
        result.push({
          id: block.id,
          componentId: block.componentId,
          answerType: ASSESSMENT_ANSWER_TYPES[block.componentId],
          label: block.title || block.name || block.label || block.placeholder || block.id,
          path: resolvePath(block.id),
          optionCount: options.length || undefined,
          options: options.length ? options.map(({ id, name, value, description }) => ({ id, name, value, description })) : undefined,
        });
      }
      visit(block.children);
    }
  };

  visit(content ?? undefined);
  return result;
}
